import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, Search, Link2, Share2, Globe } from 'lucide-react';
import { formatCompact } from '@/lib/utils';
import { useGA4TrafficAcquisition } from '@/hooks/useGA4ActivityData';
import { useAuthStore } from '@/stores/auth-store';

const CHANNEL_COLORS = {
  organic: '#06b6d4', // cyan
  direct: '#3b82f6', // blue
  referral: '#8b5cf6', // purple
  social: '#ec4899', // pink
};

const CHANNEL_ICONS = {
  organic: Search,
  direct: Link2,
  referral: Globe,
  social: Share2,
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Traffic Acquisition View - Line chart showing traffic sources over time
 */
export function TrafficAcquisitionView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: trafficData, isLoading } = useGA4TrafficAcquisition();

  // Format chart data with day names
  const chartData = useMemo(() => {
    if (!trafficData) return [];

    return trafficData.map((entry) => {
      // Parse date string (YYYYMMDD format from GA4)
      const year = parseInt(entry.date.substring(0, 4));
      const month = parseInt(entry.date.substring(4, 6)) - 1;
      const day = parseInt(entry.date.substring(6, 8));
      const date = new Date(year, month, day);
      const dayName = DAY_NAMES[date.getDay()];

      return {
        ...entry,
        dayName,
        total: entry.organic + entry.direct + entry.referral + entry.social,
      };
    });
  }, [trafficData]);

  // Calculate totals and changes
  const stats = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const totals = {
      organic: chartData.reduce((sum, d) => sum + d.organic, 0),
      direct: chartData.reduce((sum, d) => sum + d.direct, 0),
      referral: chartData.reduce((sum, d) => sum + d.referral, 0),
      social: chartData.reduce((sum, d) => sum + d.social, 0),
    };

    const grandTotal = totals.organic + totals.direct + totals.referral + totals.social;

    return {
      totals,
      grandTotal,
      percentages: {
        organic: grandTotal > 0 ? (totals.organic / grandTotal) * 100 : 0,
        direct: grandTotal > 0 ? (totals.direct / grandTotal) * 100 : 0,
        referral: grandTotal > 0 ? (totals.referral / grandTotal) * 100 : 0,
        social: grandTotal > 0 ? (totals.social / grandTotal) * 100 : 0,
      },
    };
  }, [chartData]);

  if (!hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Connect GA4 to see traffic acquisition</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            Traffic Acquisition
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Sessions by channel over the last 7 days
          </p>
        </div>

        {/* Total Sessions Badge */}
        {stats && (
          <div className="glass-panel px-4 py-2">
            <p className="text-xs text-muted-foreground">Total Sessions</p>
            <p className="text-2xl font-bold text-primary">
              {formatCompact(stats.grandTotal)}
            </p>
          </div>
        )}
      </div>

      {/* Channel Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          {(['organic', 'direct', 'referral', 'social'] as const).map((channel) => {
            const Icon = CHANNEL_ICONS[channel];
            const color = CHANNEL_COLORS[channel];
            return (
              <ChannelStatCard
                key={channel}
                icon={Icon}
                label={channel}
                value={stats.totals[channel]}
                percentage={stats.percentages[channel]}
                color={color}
              />
            );
          })}
        </div>
      )}

      {/* Chart */}
      <div className="glass-panel p-6 flex-1 min-h-[300px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading traffic data...
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No traffic data available</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {(['organic', 'direct', 'referral', 'social'] as const).map((channel) => (
                  <linearGradient key={channel} id={`gradient-${channel}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHANNEL_COLORS[channel]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHANNEL_COLORS[channel]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 15%)" />
              <XAxis
                dataKey="dayName"
                stroke="hsl(0 0% 45%)"
                tick={{ fill: 'hsl(0 0% 65%)', fontSize: 12 }}
              />
              <YAxis
                stroke="hsl(0 0% 45%)"
                tick={{ fill: 'hsl(0 0% 65%)', fontSize: 12 }}
                tickFormatter={(value) => formatCompact(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                formatter={(value) => (
                  <span className="text-foreground capitalize">{value}</span>
                )}
              />
              <Area
                type="monotone"
                dataKey="organic"
                stroke={CHANNEL_COLORS.organic}
                strokeWidth={2}
                fill={`url(#gradient-organic)`}
                name="Organic"
              />
              <Area
                type="monotone"
                dataKey="direct"
                stroke={CHANNEL_COLORS.direct}
                strokeWidth={2}
                fill={`url(#gradient-direct)`}
                name="Direct"
              />
              <Area
                type="monotone"
                dataKey="referral"
                stroke={CHANNEL_COLORS.referral}
                strokeWidth={2}
                fill={`url(#gradient-referral)`}
                name="Referral"
              />
              <Area
                type="monotone"
                dataKey="social"
                stroke={CHANNEL_COLORS.social}
                strokeWidth={2}
                fill={`url(#gradient-social)`}
                name="Social"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Channel Breakdown Bar */}
      {stats && stats.grandTotal > 0 && (
        <div className="glass-panel p-4">
          <p className="text-xs text-muted-foreground mb-2">Channel Distribution</p>
          <div className="h-3 flex rounded-full overflow-hidden">
            {(['organic', 'direct', 'referral', 'social'] as const).map((channel) => (
              <div
                key={channel}
                className="h-full transition-all"
                style={{
                  width: `${stats.percentages[channel]}%`,
                  backgroundColor: CHANNEL_COLORS[channel],
                }}
                title={`${channel}: ${stats.percentages[channel].toFixed(1)}%`}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            {(['organic', 'direct', 'referral', 'social'] as const).map((channel) => (
              <div key={channel} className="flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: CHANNEL_COLORS[channel] }}
                />
                <span className="capitalize">{channel}</span>
                <span className="text-foreground">{stats.percentages[channel].toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ChannelStatCardProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: number;
  percentage: number;
  color: string;
}

function ChannelStatCard({ icon: Icon, label, value, percentage, color }: ChannelStatCardProps) {
  return (
    <div className="glass-panel p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-sm font-medium text-foreground capitalize">{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>
        {formatCompact(value)}
      </p>
      <p className="text-xs text-muted-foreground">
        {percentage.toFixed(1)}% of traffic
      </p>
    </div>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);

  return (
    <div className="glass-panel p-3 shadow-lg border border-border">
      <p className="font-medium text-foreground mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry: any) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span style={{ color: entry.color }} className="font-medium">
              {formatCompact(entry.value)}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-border mt-2 pt-2 flex justify-between text-sm">
        <span className="text-muted-foreground">Total</span>
        <span className="font-bold text-foreground">{formatCompact(total)}</span>
      </div>
    </div>
  );
}
